import React, { useState, useRef, useEffect } from 'react';
import { PlusCircle } from 'lucide-react';

const formatRole = (role: string) => role.replace(/_/g, ' ');

interface Avatar {
  role: string;
  name?: string;
  imageUrl?: string;
}

interface ManualAvatarRequestButtonsProps {
  avatars: Avatar[];
  onRequestAll: () => void;
  onRequestSelected: (avatarRoles: string[]) => void;
  loading?: boolean;
  disabled?: boolean;
}

export function ManualAvatarRequestButtons({
  avatars,
  onRequestAll,
  onRequestSelected,
  loading = false,
  disabled = false,
}: ManualAvatarRequestButtonsProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const handleToggleRole = (role: string) => {
    const next = new Set(selectedRoles);
    if (next.has(role)) next.delete(role);
    else next.add(role);
    setSelectedRoles(next);
  };

  const handleRequestSelected = () => {
    if (selectedRoles.size > 0) {
      onRequestSelected(Array.from(selectedRoles));
      setShowDropdown(false);
      setSelectedRoles(new Set());
    }
  };

  const isDisabled = disabled || loading;
  const hasAvatars = avatars.length > 0;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        disabled={isDisabled || !hasAvatars}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all border-2 border-primary text-navy bg-white hover:bg-primary hover:text-navy shadow-sm disabled:border-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed disabled:shadow-none"
      >
        <PlusCircle size={16} />
        {loading ? 'Requesting...' : 'Request Contributions'}
      </button>

      {showDropdown && hasAvatars && (
        <div className="absolute top-full right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg min-w-[260px] z-50">
          {/* Request All option */}
          <button
            onClick={() => {
              onRequestAll();
              setShowDropdown(false);
            }}
            disabled={isDisabled}
            className="w-full text-left px-4 py-3 text-sm font-semibold text-primary hover:bg-primary/5 border-b border-slate-100 transition-colors disabled:opacity-50"
          >
            Ask All Avatars
          </button>

          {/* Individual avatar checkboxes */}
          <div className="p-2 max-h-[250px] overflow-y-auto">
            <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Or select specific avatars</p>
            {avatars.map((avatar) => (
              <label
                key={avatar.role}
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedRoles.has(avatar.role)}
                  onChange={() => handleToggleRole(avatar.role)}
                  className="size-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                />
                <span className="text-sm text-slate-700">
                  {avatar.name || formatRole(avatar.role)}
                </span>
              </label>
            ))}
          </div>

          {/* Footer actions */}
          <div className="flex gap-2 p-2 border-t border-slate-100">
            <button
              onClick={() => {
                setSelectedRoles(new Set());
                setShowDropdown(false);
              }}
              className="flex-1 px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleRequestSelected}
              disabled={selectedRoles.size === 0}
              className="flex-1 px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-lg hover:bg-primary/90 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
            >
              Request ({selectedRoles.size})
            </button>
          </div>
        </div>
      )}

      {!hasAvatars && (
        <span className="text-xs text-slate-400 ml-2">No avatars in session</span>
      )}
    </div>
  );
}
